import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { HeroReveal, FadeUp, GrowIn } from '../components/AnimatedSection';
import { useLanguage } from '../context/LanguageContext';
import SmartSummariesSection from '../components/SmartSummariesSection';
import PRODUCT_IMAGE from '../assets/scribai-hero-cutout.png';

const ScribAIExperiencePage = () => {
  const { t } = useLanguage();

  const features = [
    { title: t('experience.feature1'), desc: t('experience.feature1Desc') },
    { title: t('experience.feature2'), desc: t('experience.feature2Desc') },
    { title: t('experience.feature3'), desc: t('experience.feature3Desc') },
    { title: t('experience.feature4'), desc: t('experience.feature4Desc') },
    { title: t('experience.feature5'), desc: t('experience.feature5Desc') },
    { title: t('experience.feature6'), desc: t('experience.feature6Desc') },
  ];

  const useCases = [
    { title: t('experience.forStudents'), desc: t('experience.studentsDesc') },
    { title: t('experience.forProfessionals'), desc: t('experience.professionalsDesc') },
    { title: t('experience.forCreators'), desc: t('experience.creatorsDesc') },
  ];

  return (
    <div data-testid="scribai-experience-page" className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="min-h-screen flex items-center justify-center relative overflow-hidden px-6 pt-12">
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse 60% 40% at 50% 70%, rgba(28, 46, 74, 0.2) 0%, transparent 60%)'
          }}
        />

        <div className="relative z-10 flex flex-col items-center text-center">
          <HeroReveal delay={0.1}>
            <p className="label-small mb-4">ScribAI</p>
          </HeroReveal>

          <HeroReveal delay={0.2}>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tight mb-6">
              {t('experience.title')}
            </h1>
          </HeroReveal>

          <HeroReveal delay={0.3}>
            <p className="text-xl text-[#86868B] max-w-2xl mb-12">
              {t('experience.subtitle')}
            </p>
          </HeroReveal>

          {/* Product Image */}
          <GrowIn delay={0.4}>
            <img
              src={PRODUCT_IMAGE}
              alt="ScribAI Device"
              data-testid="hero-product-img"
              className="w-full max-w-sm"
            />
          </GrowIn>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-content bg-[#0a0a0a]">
        <div className="container-apple">
          <FadeUp>
            <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight text-center mb-4">
              {t('experience.howItWorks')}
            </h2>
          </FadeUp>
          
          <FadeUp delay={0.1}>
            <p className="text-xl text-[#86868B] text-center max-w-2xl mx-auto mb-20">
              {t('experience.workflowSubtitle')}
            </p>
          </FadeUp>

          <div className="max-w-3xl mx-auto space-y-16">
            {[
              { step: '1', title: t('experience.step1Title'), desc: t('experience.step1Desc') },
              { step: '2', title: t('experience.step2Title'), desc: t('experience.step2Desc') },
              { step: '3', title: t('experience.step3Title'), desc: t('experience.step3Desc') },
            ].map((item, index) => (
              <FadeUp key={index} delay={0.15 + index * 0.1}>
                <div className="flex gap-8 items-start" data-testid={`workflow-step-${index}`}>
                  <span className="text-6xl font-semibold text-[#333]">{item.step}</span>
                  <div>
                    <h3 className="text-2xl font-semibold text-[#F5F5F7] mb-2">{item.title}</h3>
                    <p className="text-lg text-[#86868B]">{item.desc}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Smart Summaries — real animated section, not a flat image */}
      <section className="section-content bg-[#0a0a0a]">
        <div className="container-apple">
          <SmartSummariesSection />
        </div>
      </section>

      {/* Features Grid */}
      <section className="section-content bg-black">
        <div className="container-apple">
          <FadeUp>
            <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight text-center mb-4">
              {t('experience.featuresTitle')}
            </h2>
          </FadeUp>
          
          <FadeUp delay={0.1}>
            <p className="text-xl text-[#86868B] text-center max-w-2xl mx-auto mb-20">
              {t('experience.featuresSubtitle')}
            </p>
          </FadeUp>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
            {features.map((feature, index) => (
              <FadeUp key={index} delay={0.1 + index * 0.05}>
                <div data-testid={`feature-${index}`}>
                  <h3 className="text-xl font-semibold text-[#F5F5F7] mb-2">{feature.title}</h3>
                  <p className="text-[#86868B]">{feature.desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="section-content bg-[#0a0a0a]">
        <div className="container-apple">
          <FadeUp>
            <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight text-center mb-4">
              {t('experience.useCasesTitle')}
            </h2>
          </FadeUp>
          
          <FadeUp delay={0.1}>
            <p className="text-xl text-[#86868B] text-center max-w-2xl mx-auto mb-16">
              {t('experience.useCasesSubtitle')}
            </p>
          </FadeUp>

          <div className="grid md:grid-cols-3 gap-8">
            {useCases.map((useCase, index) => (
              <FadeUp key={index} delay={0.15 + index * 0.1}>
                <div 
                  className="p-8 rounded-2xl bg-[#111] border border-[#222]"
                  data-testid={`use-case-${index}`}
                >
                  <h3 className="text-xl font-semibold text-[#F5F5F7] mb-3">{useCase.title}</h3>
                  <p className="text-[#86868B]">{useCase.desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Specs Highlight */}
      <section className="section-content bg-black">
        <div className="container-apple">
          <FadeUp>
            <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight text-center mb-20">
              {t('experience.specsTitle')}
            </h2>
          </FadeUp>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-3xl mx-auto text-center">
            {[
              { value: '30hrs', label: t('experience.recording') },
              { value: '0.96oz', label: t('experience.weight') },
              { value: '48kHz', label: t('experience.audioQuality') },
              { value: '3.1mm', label: t('experience.thin') },
            ].map((spec, index) => (
              <FadeUp key={index} delay={0.1 + index * 0.05}>
                <div data-testid={`spec-highlight-${index}`}>
                  <p className="text-3xl sm:text-4xl font-semibold text-[#F5F5F7] mb-1">{spec.value}</p>
                  <p className="text-sm text-[#86868B]">{spec.label}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 bg-[#0a0a0a]">
        <div className="container-apple text-center">
          <FadeUp>
            <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight mb-6">
              {t('experience.ctaTitle')}
            </h2>
          </FadeUp>
          
          <FadeUp delay={0.1}>
            <p className="text-xl text-[#86868B] mb-10 max-w-xl mx-auto">
              {t('experience.ctaSubtitle')}
            </p>
          </FadeUp>
          
          <FadeUp delay={0.2}>
            <Link 
              to="/product" 
              className="btn-secondary text-xl"
              data-testid="cta-buy"
            >
              {t('nav.buy')} ScribAI <ChevronRight size={20} />
            </Link>
          </FadeUp>
        </div>
      </section>
    </div>
  );
};

export default ScribAIExperiencePage;
